import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';

import { setVehicleActive } from '../../redux/slices/vehiclesSlice';
import { isVehicleActive } from '../../utils/vehicleActive';

/**
 * Bestätigungsdialog „Als Reserve deaktivieren" / „Aktivieren".
 *
 * Die eigentliche Prüfung (Kontingent beim Aktivieren, keine Belegung beim
 * Deaktivieren) macht die CF `setVehicleActive`; deren deutsche Meldung wird
 * hier im Dialog angezeigt, statt die ganze Seite in einen Fehlerzustand zu
 * versetzen.
 */
const VehicleActiveDialog = ({ vehicle, open, onClose }) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setError('');
  }, [open]);

  if (!vehicle) return null;

  const currentlyActive = isVehicleActive(vehicle);
  const label = `${vehicle.vehicleNumber ? `Nr. ${vehicle.vehicleNumber} · ` : ''}${vehicle.model || ''} (${vehicle.licensePlate || ''})`;
  const isOccupied = currentlyActive && (vehicle.inUse === true || !!vehicle.driverId);

  const handleConfirm = async () => {
    setSaving(true);
    setError('');
    try {
      await dispatch(setVehicleActive({ id: vehicle.id, active: !currentlyActive })).unwrap();
      onClose();
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Die Änderung konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {currentlyActive ? 'Fahrzeug als Reserve deaktivieren' : 'Reservefahrzeug aktivieren'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body1" gutterBottom>
            {label}
          </Typography>
          {currentlyActive ? (
            <Typography variant="body2" color="text.secondary">
              Das Fahrzeug bleibt mit allen Daten angelegt, ist in der Fahrer-App aber nicht
              mehr wählbar und belegt keinen Platz im Fahrzeugkontingent. So können Sie z. B.
              für ein defektes Auto einen Ersatzwagen aktivieren und das Auto später wieder
              einschalten, ohne es neu anzulegen.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Das Fahrzeug wird wieder in der Fahrer-App wählbar und belegt einen Platz im
              Fahrzeugkontingent. Ist das Kontingent ausgeschöpft, deaktivieren Sie zuerst ein
              anderes Fahrzeug.
            </Typography>
          )}
          {isOccupied && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Das Fahrzeug ist gerade von einem Fahrer belegt. Geben Sie es zuerst frei.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Abbrechen</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={currentlyActive ? 'warning' : 'success'}
          disabled={saving || isOccupied}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {currentlyActive ? 'Deaktivieren' : 'Aktivieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleActiveDialog;
